import React, { useState } from 'react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Textarea } from '../../../components/ui/Textarea'
import { Label } from '../../../components/ui/Label'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import { 
  X, 
  Send, 
  CheckCircle, 
  AlertCircle,
  Building2,
  Users,
  Target,
  TrendingUp
} from 'lucide-react'
import { communicationService, CommunicationServiceError } from '../../../services/communicationService'
import { cn } from '../../../lib/utils'

interface CustomIndustryRequestModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit?: (requestData: CustomIndustryRequestData) => Promise<void>
}

export interface CustomIndustryRequestData {
  industryName: string
  companyName: string
  contactEmail: string
  businessDescription: string
  specificNeeds: string
  companySize: string
  urgency: 'low' | 'medium' | 'high'
}

export const CustomIndustryRequestModal: React.FC<CustomIndustryRequestModalProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<CustomIndustryRequestData>({
    industryName: '',
    companyName: '',
    contactEmail: '',
    businessDescription: '',
    specificNeeds: '',
    companySize: '',
    urgency: 'medium'
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errors, setErrors] = useState<Partial<CustomIndustryRequestData>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  const validateForm = (): boolean => {
    const newErrors: Partial<CustomIndustryRequestData> = {}
    
    if (!formData.industryName.trim()) {
      newErrors.industryName = 'Industry name is required'
    }
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required'
    }
    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.contactEmail)) {
      newErrors.contactEmail = 'Please enter a valid email address'
    }
    if (!formData.businessDescription.trim()) {
      newErrors.businessDescription = 'Business description is required'
    }
    if (!formData.specificNeeds.trim()) {
      newErrors.specificNeeds = 'Please describe your intelligence needs'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsSubmitting(true)
    setSubmitError(null)
    
    try {
      if (onSubmit) {
        await onSubmit(formData)
      } else {
        // Use communication service for dual email flow
        await communicationService.sendCustomIndustrySubmission(formData)
      }
      
      // Log success for debugging
      console.log('Custom industry request submitted successfully:', {
        ...formData,
        submittedAt: new Date().toISOString()
      })
      
      setIsSuccess(true)
      setTimeout(() => {
        onClose()
        resetForm()
      }, 2000)
    } catch (error) {
      console.error('Error submitting custom industry request:', error)
      
      if (error instanceof CommunicationServiceError) {
        setSubmitError(
          error.code === 'RATE_LIMIT_EXCEEDED' 
            ? 'Too many requests. Please try again in a few minutes.'
            : error.message
        )
      } else {
        setSubmitError('Failed to submit your request. Please try again or contact us directly at support@stratiqx.ai.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setFormData({
      industryName: '',
      companyName: '',
      contactEmail: '',
      businessDescription: '',
      specificNeeds: '',
      companySize: '',
      urgency: 'medium'
    })
    setErrors({})
    setIsSuccess(false)
    setIsSubmitting(false)
    setSubmitError(null)
  }

  const handleInputChange = (field: keyof CustomIndustryRequestData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  if (!isOpen) return null

  if (isSuccess) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Request Submitted Successfully!
            </h3>
            <p className="text-gray-600 mb-4">
              We'll review your custom industry request and get back to you within 1-2 business days.
            </p>
            <p className="text-sm text-gray-500">
              Check your email for confirmation details.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-xl flex flex-col max-h-[90vh]">
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Request Custom Industry Configuration
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              We'll create a tailored intelligence setup for your industry
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-blue-900 mb-1">
                    Custom Intelligence Configuration
                  </h4>
                  <p className="text-sm text-blue-800">
                    We'll create a tailored intelligence monitoring setup specifically for your industry, 
                    including relevant competitors, suppliers, and market dynamics.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Industry Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-600" />
                  Industry Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="industryName">Industry Name *</Label>
                    <Input
                      id="industryName"
                      placeholder="e.g., Renewable Energy, EdTech, Fintech"
                      value={formData.industryName}
                      onChange={(e) => handleInputChange('industryName', e.target.value)}
                      className={errors.industryName ? 'border-red-300' : ''}
                    />
                    {errors.industryName && (
                      <p className="text-sm text-red-600">{errors.industryName}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companySize">Company Size</Label>
                    <select
                      id="companySize"
                      value={formData.companySize}
                      onChange={(e) => handleInputChange('companySize', e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select company size</option>
                      <option value="startup">Startup (1-10 employees)</option>
                      <option value="small">Small (11-50 employees)</option>
                      <option value="medium">Medium (51-250 employees)</option>
                      <option value="large">Large (251-1000 employees)</option>
                      <option value="enterprise">Enterprise (1000+ employees)</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="businessDescription">Business Description *</Label>
                  <Textarea
                    id="businessDescription"
                    placeholder="Describe your business model, main products/services, and target market..."
                    value={formData.businessDescription}
                    onChange={(e) => handleInputChange('businessDescription', e.target.value)}
                    className={cn("min-h-[80px] resize-none", errors.businessDescription ? 'border-red-300' : '')}
                  />
                  {errors.businessDescription && (
                    <p className="text-sm text-red-600">{errors.businessDescription}</p>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  Contact Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyName">Company Name *</Label>
                    <Input
                      id="companyName"
                      placeholder="Your company name"
                      value={formData.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      className={errors.companyName ? 'border-red-300' : ''}
                    />
                    {errors.companyName && (
                      <p className="text-sm text-red-600">{errors.companyName}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contactEmail">Email Address *</Label>
                    <Input
                      id="contactEmail"
                      type="email"
                      placeholder="your.email@company.com"
                      value={formData.contactEmail}
                      onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                      className={errors.contactEmail ? 'border-red-300' : ''}
                    />
                    {errors.contactEmail && (
                      <p className="text-sm text-red-600">{errors.contactEmail}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Intelligence Requirements */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  Intelligence Requirements
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="specificNeeds">Specific Intelligence Needs *</Label>
                  <Textarea
                    id="specificNeeds"
                    placeholder="What specific market intelligence, competitor tracking, or industry insights do you need? Include any particular metrics, data sources, or reporting requirements..."
                    value={formData.specificNeeds}
                    onChange={(e) => handleInputChange('specificNeeds', e.target.value)}
                    className={cn("min-h-[100px] resize-none", errors.specificNeeds ? 'border-red-300' : '')}
                  />
                  {errors.specificNeeds && (
                    <p className="text-sm text-red-600">{errors.specificNeeds}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="urgency">Priority Level</Label>
                  <select
                    id="urgency"
                    value={formData.urgency}
                    onChange={(e) => handleInputChange('urgency', e.target.value as 'low' | 'medium' | 'high')}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="low">Low - No immediate timeline</option>
                    <option value="medium">Medium - Within 2-4 weeks</option>
                    <option value="high">High - ASAP (within 1 week)</option>
                  </select>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Error Display */}
        {submitError && (
          <div className="p-6 bg-red-50 border-t border-red-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-red-900 mb-1">
                  Unable to Submit Request
                </h4>
                <p className="text-sm text-red-800">{submitError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Fixed Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              * Required fields
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit Request
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}